/**
 * EXPORT mode's footer strip: the file name and the button that writes the GLB.
 *
 * The mode's whole point is one action, so that action does not scroll: the footer sits under the
 * right column and stays put whatever the panels above it do (audit B27 — the name used to be a
 * bare narrow input in the middle of a scrolling column).
 *
 * With no project open there is nothing to export and the strip renders NOTHING, which collapses
 * it to zero height (`.col-foot:not(:has(.panel > *))` in `style.css`). EXPORT is gated on a bound
 * rig, so in practice that state is never reached from the UI.
 */

import type { EditorContext } from '../context';
import type { Controllers } from '../controllers/types';
import { button, el, icon, textInput } from './dom';
import { createPanel, field, note, type PanelHandle } from './panel';

export function mountExportFooterPanel(
  container: HTMLElement,
  ctx: EditorContext,
  controllers: Controllers,
): PanelHandle {
  const { store, session } = ctx;
  let exporting = false;
  /** The typed filename, or `null` while it still follows the project name. */
  let typedName: string | null = null;
  let namedProjectId: string | null = null;

  function render(root: HTMLElement): void {
    const state = store.getState();
    if (!state.project) return;
    const ticked = state.library.selectedClipIds.length;

    // A different project means the typed name belonged to a character that is no longer open.
    const projectId = state.project.id;
    if (projectId !== namedProjectId) {
      namedProjectId = projectId;
      typedName = null;
    }
    /**
     * Read at CLICK time, never captured at render time. `textInput` commits on `change`, which
     * fires as the user blurs into the export button — in the same turn, before any rebuild — so
     * a filename closed over during render is always the previous one (it downloaded "Knight.glb"
     * right after typing "knight-final").
     */
    function currentFilename(): string {
      return (typedName ?? store.getState().project?.name ?? '').trim();
    }

    root.appendChild(
      field(
        'File name',
        textInput(
          currentFilename(),
          (value) => {
            typedName = value;
          },
          { 'data-role': 'export-filename', title: '".glb" is added for you' },
        ),
      ),
    );

    /**
     * Both buttons run the same export; `stripTextures` is the only difference. Written once here
     * so the busy flag, the filename read and the error handling cannot drift between them.
     */
    function runExport(stripTextures: boolean): void {
      if (exporting) return;
      exporting = true;
      instance.schedule();
      const done = (): void => {
        exporting = false;
        instance.schedule();
      };
      // What the field shows is what gets written; a blank field falls back to the project
      // name inside the controller rather than exporting a file called ".glb".
      controllers.export
        .exportGlbDownload(currentFilename() || undefined, { stripTextures })
        .then(done, done);
    }

    // "Export GLB with 1 clips" was on the audit's list (B27); the plural is counted.
    const clipLabel =
      ticked === 0 ? 'GLB (rig-only)' : `GLB with ${ticked} clip${ticked === 1 ? '' : 's'}`;

    root.appendChild(
      el('div', { class: 'buttons' }, [
        button(
          exporting ? [icon('download'), 'Exporting…'] : [icon('download'), `Export ${clipLabel}`],
          () => runExport(false),
          {
            class: 'primary',
            'data-action': 'export-glb',
            disabled: exporting,
            title:
              ticked === 0
                ? 'Write a rig-only GLB — animations ship as clip files'
                : 'Write a GLB with the ticked clips baked in',
          },
        ),
        /*
         * A second BUTTON rather than a checkbox on the first: these are two different deliverables
         * — one file versus a GLB plus a manifest plus N images — and a tick box that silently
         * turns one click into a multi-download prompt reads as a bug the first time it happens.
         * Its own `data-action` so the existing `export-glb` selectors keep meaning the normal
         * export (`.claude/vault/ui-editor-dna.md`: panel hooks are frozen on purpose).
         */
        button(
          exporting ? [icon('download'), 'Exporting…'] : [icon('download'), 'Without textures'],
          () => runExport(true),
          {
            'data-action': 'export-glb-no-textures',
            disabled: exporting,
            title:
              'Write the same GLB with UVs but no images, plus the texture files and a ' +
              'manifest — for a game that shows the character first and streams textures in',
          },
        ),
      ]),
    );

    // The mode gate makes this unreachable; it stays as a last line of defence rather than a
    // silent failure if some future path enters EXPORT without a rig.
    if (!session.rig) root.appendChild(note('No rig bound — the export will fail.', 'note bad'));
  }

  const instance = createPanel(container, ctx, render, {
    className: 'export-footer',
    slices: [(s) => s.library.selectedClipIds, (s) => s.project, (s) => s.ui.lastExport],
    events: ['rig-changed', 'model-changed'],
  });

  return instance;
}
